import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Clock, CheckCircle, XCircle, Plus, Edit, Trash2, CheckSquare, Send, Copy, MessageCircle, FileDown, RefreshCw, Upload, Loader2 } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { fmtDate } from '../lib/dateUtils';

// Formatta ora da timestamp - gestisce sia ISO UTC che locale
function formatTime(isoString) {
  if (!isoString) return '-';
  try {
    // Se contiene T e NON contiene Z/+/offset, e' gia' locale (naive)
    if (isoString.includes('T') && !isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}:\d{2}$/)) {
      return isoString.split('T')[1].substring(0, 5);
    }
    // Forza display in fuso orario Europe/Rome (indipendente dalla TZ del browser/server)
    return new Date(isoString).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });
  } catch { return '-'; }
}

const MONTHS_IT_LOWER = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

export default function AttendancePage() {
  const { user, isRole } = useAuth();
  const canSheet = isRole('admin', 'aspp', 'qhse', 'capo_cantiere');
  const monthOptions = Array.from({ length: 13 }, (_, i) => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
    return { value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: `${MONTHS_IT_LOWER[d.getMonth()]} ${d.getFullYear()}` };
  });
  const [showSheet, setShowSheet] = useState(false);
  const [sheetMonth, setSheetMonth] = useState(() => { const d = new Date(); if (d.getDate() <= 10) d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; });
  const [sheetBusy, setSheetBusy] = useState(false);
  const [sheetMsg, setSheetMsg] = useState(null);
  const [tsStatus, setTsStatus] = useState(null);
  const tsFileRef = useRef(null);
  const [otMap, setOtMap] = useState({});

  // hours = 0 toglie l'autorizzazione; il conteggio nel foglio segue subito
  const setOvertime = async (r, hours) => {
    const key = `${r.employee_id}|${r.date}`;
    const h = Math.max(0, Math.min(8, Math.floor(Number(hours) || 0)));
    try {
      await api.put('/attendance-sheet/overtime', { employee_id: r.employee_id, date: r.date, hours: h });
      setOtMap((prev) => { const n = { ...prev }; if (h > 0) n[key] = { ...(prev[key] || {}), hours: h }; else delete n[key]; return n; });
    } catch (err) { alert(err.response?.data?.detail || 'Salvataggio non riuscito'); }
  };

  const loadTurnstileStatus = async () => {
    try { setTsStatus((await api.get('/turnstile/status')).data); } catch { /* ignore */ }
  };

  const downloadSheet = async () => {
    setSheetBusy(true); setSheetMsg(null);
    try {
      const r = await api.get('/attendance-sheet/monthly', { params: { month: sheetMonth }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `Foglio_presenze_${sheetMonth}.docx`; a.click();
      URL.revokeObjectURL(url);
    } catch { setSheetMsg({ ok: false, text: 'Download non riuscito, riprova.' }); } finally { setSheetBusy(false); }
  };

  const refreshTurnstile = async (auto = false) => {
    try {
      const r = (await api.post('/turnstile/refresh', null, { params: { auto } })).data;
      if (!r.started) {
        if (!auto) setSheetMsg(r.reason === 'not_configured'
          ? { ok: false, text: 'Aggiornamento da email non ancora collegato: chiedi all\'amministratore di completare "Collega email" in Ordini Cliente.' }
          : { ok: true, text: 'Aggiornamento gia in corso o appena eseguito.' });
        return;
      }
      setSheetBusy(true);
      let st = null;
      for (let i = 0; i < 40; i++) {
        await new Promise(res => setTimeout(res, 4000));
        st = (await api.get('/turnstile/refresh/status')).data;
        if (!st.running) break;
      }
      await loadTurnstileStatus();
      if (st?.last_refresh_status === 'errore') setSheetMsg({ ok: false, text: st.last_refresh_error || 'Aggiornamento non riuscito' });
      else if (!auto) setSheetMsg({ ok: true, text: 'Dati dei tornelli aggiornati dalle email.' });
    } catch (err) {
      if (!auto) setSheetMsg({ ok: false, text: err.response?.data?.detail || 'Aggiornamento non riuscito' });
    } finally { setSheetBusy(false); }
  };

  const uploadTurnstile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSheetBusy(true); setSheetMsg(null);
    try {
      const fd = new FormData(); files.forEach(f => fd.append('files', f));
      const res = (await api.post('/turnstile/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data.results || [];
      const rows = res.reduce((s, r) => s + (r.new || 0), 0);
      const errs = res.filter(r => r.status === 'errore');
      const unmatched = Array.from(new Set(res.flatMap(r => r.unmatched || [])));
      setSheetMsg({ ok: errs.length === 0, text: `${rows} nuove timbrature importate da ${res.length} file.${errs.length ? ` ${errs.length} file non letti: ${errs[0].detail}.` : ''}${unmatched.length ? ` Nomi non riconosciuti (non sono dipendenti): ${unmatched.join(', ')}.` : ''}` });
      loadTurnstileStatus();
    } catch (err) {
      setSheetMsg({ ok: false, text: err.response?.data?.detail || 'Caricamento non riuscito' });
    } finally { setSheetBusy(false); if (tsFileRef.current) tsFileRef.current.value = ''; }
  };

  useEffect(() => { if (canSheet) refreshTurnstile(true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const [records, setRecords] = useState([]);
  const [todayData, setTodayData] = useState(null);
  const [myToday, setMyToday] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [projects, setProjects] = useState([]);
  
  // Manager dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkForm, setBulkForm] = useState({ clock_in: '', break_start: '', break_end: '', clock_out: '', site_id: '' });

  // Conferma Ore mensile
  const [hcPeriod, setHcPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [hcSelectedEmployees, setHcSelectedEmployees] = useState([]);
  const [hcRequesting, setHcRequesting] = useState(false);
  const [hcResult, setHcResult] = useState(null);
  const [hcList, setHcList] = useState([]);
  const [hcLoadingList, setHcLoadingList] = useState(false);

  // Riepilogo Presenze - vista mensile filtrabile per cantiere/dipendente
  const [sumPeriod, setSumPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [sumSiteId, setSumSiteId] = useState('__all__');
  const [sumEmployeeId, setSumEmployeeId] = useState('__all__');
  const [sumRows, setSumRows] = useState([]);
  const [sumLoading, setSumLoading] = useState(false);
  
  const [form, setForm] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    clock_in: '',
    clock_out: '',
    break_start: '',
    break_end: '',
    hours_worked: '',
    site_id: '',
    notes: ''
  });

  const load = async () => {
    try {
      const [recRes, summaryRes, empRes, sitesRes, projRes] = await Promise.all([
        api.get('/attendance', { params: { date: selectedDate } }),
        api.get('/attendance/today-summary').catch(() => ({ data: null })),
        api.get('/employees').catch(() => ({ data: [] })),
        api.get('/sites').catch(() => ({ data: [] })),
        api.get('/projects').catch(() => ({ data: [] })),
      ]);
      setRecords(recRes.data);
      setTodayData(summaryRes.data);
      setEmployees(empRes.data.filter(e => e.is_active));
      setSites(sitesRes.data);
      setProjects(projRes.data || []);
      if (user?.employee_id) {
        try {
          const myRes = await api.get('/attendance/my-today');
          setMyToday(myRes.data);
        } catch {}
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [selectedDate]);

  const handleClockIn = async () => {
    try {
      await api.post('/attendance/clock-in', {});
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleBreakStart = async () => {
    try {
      await api.post('/attendance/break-start', {});
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleBreakEnd = async () => {
    try {
      await api.post('/attendance/break-end', {});
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleClockOut = async () => {
    try {
      await api.post('/attendance/clock-out', {});
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const openCreate = () => {
    setForm({
      employee_id: '',
      date: selectedDate,
      clock_in: '',
      clock_out: '',
      break_start: '',
      break_end: '',
      hours_worked: '',
      site_id: '',
      notes: ''
    });
    setShowCreate(true);
  };

  const handleCreate = async () => {
    try {
      let break_minutes = null;
      if (form.break_start && form.break_end) {
        const bs = new Date(`2000-01-01T${form.break_start}:00`);
        const be = new Date(`2000-01-01T${form.break_end}:00`);
        break_minutes = Math.round((be - bs) / 60000);
      }
      // Non aggiungere Z - l'admin inserisce orari locali, non UTC
      const payload = {
        employee_id: form.employee_id,
        date: form.date,
        clock_in: form.clock_in ? `${form.date}T${form.clock_in}:00` : null,
        clock_out: form.clock_out ? `${form.date}T${form.clock_out}:00` : null,
        break_start: form.break_start ? `${form.date}T${form.break_start}:00` : null,
        break_end: form.break_end ? `${form.date}T${form.break_end}:00` : null,
        break_minutes: break_minutes,
        hours_worked: form.hours_worked ? parseFloat(form.hours_worked) : null,
        site_id: form.site_id || null,
        notes: form.notes
      };
      await api.post('/attendance', payload);
      setShowCreate(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore durante la creazione');
    }
  };

  const openEdit = (record) => {
    setEditRecord(record);
    // Estrai ora dal timestamp - gestisce sia UTC che locale
    const extractTime = (isoString) => {
      if (!isoString) return '';
      try {
        // Se contiene offset timezone (Z o +/-HH:MM), converti a ora locale
        if (isoString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoString)) {
          const d = new Date(isoString);
          const hh = String(d.getHours()).padStart(2, '0');
          const mm = String(d.getMinutes()).padStart(2, '0');
          return `${hh}:${mm}`;
        }
        // Altrimenti e' gia' locale, estrai direttamente HH:MM
        if (isoString.includes('T')) {
          return isoString.split('T')[1].substring(0, 5);
        }
        return '';
      } catch { return ''; }
    };
    
    setForm({
      employee_id: record.employee_id || '',
      date: record.date || selectedDate,
      clock_in: extractTime(record.clock_in),
      clock_out: extractTime(record.clock_out),
      break_start: extractTime(record.break_start),
      break_end: extractTime(record.break_end),
      hours_worked: record.hours_worked?.toString() || '',
      site_id: record.site_id || '',
      notes: record.notes || '',
      project_hours: record.project_hours || []
    });
    setShowEdit(true);
  };

  const handleEdit = async () => {
    try {
      let break_minutes = null;
      if (form.break_start && form.break_end) {
        const bs = new Date(`2000-01-01T${form.break_start}:00`);
        const be = new Date(`2000-01-01T${form.break_end}:00`);
        break_minutes = Math.round((be - bs) / 60000);
      }
      // Non aggiungere Z - l'admin inserisce orari locali
      const payload = {
        date: form.date,
        clock_in: form.clock_in ? `${form.date}T${form.clock_in}:00` : null,
        clock_out: form.clock_out ? `${form.date}T${form.clock_out}:00` : null,
        break_start: form.break_start ? `${form.date}T${form.break_start}:00` : null,
        break_end: form.break_end ? `${form.date}T${form.break_end}:00` : null,
        break_minutes: break_minutes,
        hours_worked: form.hours_worked ? parseFloat(form.hours_worked) : null,
        site_id: form.site_id || null,
        notes: form.notes,
        project_hours: (form.project_hours || []).filter(ph => ph.project_id && ph.hours)
      };
      await api.put(`/attendance/${editRecord.id}`, payload);
      setShowEdit(false);
      setEditRecord(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore durante la modifica');
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Sei sicuro di voler eliminare la presenza di ${record.employee_name}?`)) return;
    try {
      await api.delete(`/attendance/${record.id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => setSelectedIds(prev => prev.length === records.length ? [] : records.map(r => r.id));

  const handleBulkDelete = async () => {
    if (!window.confirm(`Eliminare ${selectedIds.length} presenze selezionate?`)) return;
    try {
      await Promise.all(selectedIds.map(id => api.delete(`/attendance/${id}`)));
      setSelectedIds([]);
      load();
    } catch (err) { alert('Errore eliminazione bulk'); }
  };

  const handleBulkEdit = async () => {
    const updates = {};
    if (bulkForm.clock_in) updates.clock_in = `${selectedDate}T${bulkForm.clock_in}:00`;
    if (bulkForm.clock_out) updates.clock_out = `${selectedDate}T${bulkForm.clock_out}:00`;
    if (bulkForm.break_start) updates.break_start = `${selectedDate}T${bulkForm.break_start}:00`;
    if (bulkForm.break_end) updates.break_end = `${selectedDate}T${bulkForm.break_end}:00`;
    if (bulkForm.break_start && bulkForm.break_end) {
      const bs = new Date(`2000-01-01T${bulkForm.break_start}:00`);
      const be = new Date(`2000-01-01T${bulkForm.break_end}:00`);
      updates.break_minutes = Math.round((be - bs) / 60000);
    }
    if (bulkForm.site_id) updates.site_id = bulkForm.site_id === '__none__' ? '' : bulkForm.site_id;
    if (Object.keys(updates).length === 0) { alert('Compila almeno un campo'); return; }
    try {
      await Promise.all(selectedIds.map(id => api.put(`/attendance/${id}`, updates)));
      setShowBulkEdit(false);
      setSelectedIds([]);
      setBulkForm({ clock_in: '', break_start: '', break_end: '', clock_out: '', site_id: '' });
      load();
    } catch (err) { alert('Errore modifica bulk'); }
  };

  const loadHoursConfirmations = async (period) => {
    setHcLoadingList(true);
    try {
      const r = await api.get('/hours-confirmation', { params: { period } });
      setHcList(r.data);
    } catch { setHcList([]); }
    finally { setHcLoadingList(false); }
  };

  useEffect(() => {
    if (isRole('admin', 'qhse', 'capo_cantiere')) loadHoursConfirmations(hcPeriod);
    // eslint-disable-next-line
  }, [hcPeriod]);

  const toggleHcEmployee = (id) => setHcSelectedEmployees(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const requestHoursConfirmation = async () => {
    setHcRequesting(true);
    setHcResult(null);
    try {
      const r = await api.post('/hours-confirmation/request', { period: hcPeriod, employee_ids: hcSelectedEmployees });
      setHcResult(r.data);
      setHcSelectedEmployees([]);
      loadHoursConfirmations(hcPeriod);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore richiesta conferma ore');
    } finally {
      setHcRequesting(false);
    }
  };

  const copyWhatsappMessage = () => {
    if (!hcResult?.whatsapp_message) return;
    navigator.clipboard.writeText(hcResult.whatsapp_message).then(() => alert('Messaggio copiato! Incollalo nel gruppo WhatsApp.'));
  };

  const markHcReviewed = async (id) => {
    await api.put(`/hours-confirmation/${id}/review`);
    loadHoursConfirmations(hcPeriod);
  };

  const HC_STATUS_MAP = {
    pending: { label: 'In attesa', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
    confirmed: { label: 'Confermate', cls: 'bg-green-50 text-green-700 border-green-200' },
    disputed: { label: 'Da rivedere', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  };

  const loadSummary = async () => {
    setSumLoading(true);
    try {
      const [y, m] = sumPeriod.split('-').map(Number);
      const lastDay = new Date(y, m, 0).getDate();
      const dateFrom = `${sumPeriod}-01`;
      const dateTo = `${sumPeriod}-${String(lastDay).padStart(2, '0')}`;
      const attParams = { date_from: dateFrom, date_to: dateTo };
      if (sumSiteId !== '__all__') attParams.site_id = sumSiteId;
      if (sumEmployeeId !== '__all__') attParams.employee_id = sumEmployeeId;
      const tsParams = { date_from: dateFrom, date_to: dateTo };
      if (sumEmployeeId !== '__all__') tsParams.employee_id = sumEmployeeId;
      const [attRes, hcRes, tsRes, otRes] = await Promise.all([
        api.get('/attendance', { params: attParams }),
        api.get('/hours-confirmation', { params: { period: sumPeriod } }),
        sumSiteId === '__all__' ? api.get('/turnstile/records', { params: tsParams }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        api.get('/attendance-sheet/overtime', { params: { month: sumPeriod } }).catch(() => ({ data: [] })),
      ]);
      const ot = {};
      (otRes.data || []).forEach((o) => { ot[`${o.employee_id}|${o.date}`] = o; });
      setOtMap(ot);

      const corrByKey = {};
      const statusByEmp = {};
      (hcRes.data || []).forEach((c) => {
        statusByEmp[c.employee_id] = c.status;
        (c.corrections || []).forEach((cor) => { corrByKey[`${c.employee_id}|${cor.date}`] = cor; });
      });

      const rows = (attRes.data || []).map((r) => ({
        date: r.date, employee_id: r.employee_id, employee_name: r.employee_name,
        site_name: r.site_name || '', hours_worked: r.hours_worked, manual: !!r.created_by, created_by_name: r.created_by_name,
        correction: corrByKey[`${r.employee_id}|${r.date}`] || null,
        confirmation_status: statusByEmp[r.employee_id] || null,
      }));
      const seen = new Set(rows.map((r) => `${r.employee_id}|${r.date}`));

      // Aggiungi anche i giorni aggiunti dai dipendenti che non avevano nessuna timbratura originale
      if (sumSiteId === '__all__') {
        (hcRes.data || []).forEach((c) => {
          if (sumEmployeeId !== '__all__' && c.employee_id !== sumEmployeeId) return;
          (c.corrections || []).forEach((cor) => {
            const key = `${c.employee_id}|${cor.date}`;
            if (!seen.has(key)) {
              seen.add(key);
              rows.push({
                date: cor.date, employee_id: c.employee_id, employee_name: c.employee_name,
                site_name: '', hours_worked: null, correction: cor, confirmation_status: c.status,
              });
            }
          });
        });
      }

      // Timbrature dei tornelli Sarpom (importate dalle email): una riga per dipendente e giorno
      const tsByDay = {};
      (tsRes.data || []).forEach((t) => {
        const key = `${t.employee_id || t.name}|${t.date}`;
        const g = tsByDay[key] || (tsByDay[key] = { date: t.date, employee_id: t.employee_id, employee_name: t.employee_name, site_name: t.area || '', min: 0, first_in: t.in_at, last_out: t.out_at });
        g.min += t.effective_min || 0;
        if (t.in_at < g.first_in) g.first_in = t.in_at;
        if (t.out_at > g.last_out) g.last_out = t.out_at;
      });
      Object.values(tsByDay).forEach((g) => rows.push({
        date: g.date, employee_id: g.employee_id || `ts-${g.employee_name}`, employee_name: g.employee_name, site_name: g.site_name,
        hours_worked: Math.round((g.min / 60) * 100) / 100, correction: null, confirmation_status: null,
        turnstile: { in_at: g.first_in, out_at: g.last_out },
      }));

      // Una sola riga per dipendente e giorno, quella che fa fede (stessa priorita' del foglio presenze):
      // presenza manuale di un responsabile > tornello Sarpom > timbratura del dipendente.
      const manualKeys = new Set(rows.filter((r) => r.manual && r.hours_worked > 0).map((r) => `${r.employee_id}|${r.date}`));
      const filtered = rows.filter((r) => {
        const k = `${r.employee_id}|${r.date}`;
        if (r.turnstile) return !manualKeys.has(k);
        if (manualKeys.has(k)) return r.manual && r.hours_worked > 0;
        return !tsByDay[k];
      });
      rows.length = 0; rows.push(...filtered);

      rows.sort((a, b) => a.date === b.date ? (a.employee_name || '').localeCompare(b.employee_name || '') : a.date.localeCompare(b.date));
      setSumRows(rows);
    } catch (err) {
      console.error(err);
      setSumRows([]);
    } finally {
      setSumLoading(false);
    }
  };

  useEffect(() => {
    if (isRole('admin', 'aspp', 'qhse', 'capo_cantiere')) loadSummary();
    // eslint-disable-next-line
  }, [sumPeriod, sumSiteId, sumEmployeeId]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Presenze</h1>
        {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => { setShowSheet(true); setSheetMsg(null); loadTurnstileStatus(); }} data-testid="sheet-btn">
              <FileDown size={16} className="mr-1" /> Foglio presenze (Word)
            </Button>
            <Button onClick={openCreate} data-testid="add-attendance-btn">
              <Plus size={16} className="mr-1" /> Nuova Presenza
            </Button>
          </div>
        )}
      </div>

      {/* Clock In/Out for ALL users with employee_id (including managers) */}
      {user?.employee_id && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                <Clock size={24} className="text-slate-600" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <p className="text-base font-semibold text-slate-900">La Mia Timbratura di Oggi</p>
                {myToday?.clock_in ? (
                  <p className="text-sm text-slate-500">
                    Entrata: {formatTime(myToday.clock_in)}
                    {myToday.break_start && ` | Pausa: ${formatTime(myToday.break_start)}`}
                    {myToday.break_end && ` - ${formatTime(myToday.break_end)}`}
                    {myToday.break_minutes != null && ` (${myToday.break_minutes} min)`}
                    {myToday.clock_out && ` | Uscita: ${formatTime(myToday.clock_out)}`}
                    {myToday.hours_worked && ` | ${myToday.hours_worked}h nette`}
                  </p>
                ) : (
                  <p className="text-sm text-slate-500">Nessuna timbratura effettuata</p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap justify-center">
                {/* Step 1: Timbra Entrata */}
                {!myToday?.clock_in && (
                  <Button onClick={handleClockIn} className="bg-green-600 hover:bg-green-700" data-testid="clock-in-btn">Timbra Entrata</Button>
                )}
                {/* Step 2: Inizio Pausa (solo se entrata fatta e pausa non iniziata) */}
                {myToday?.clock_in && !myToday?.break_start && !myToday?.clock_out && (
                  <Button onClick={handleBreakStart} className="bg-amber-500 hover:bg-amber-600" data-testid="break-start-btn">Inizio Pausa</Button>
                )}
                {/* Step 3: Fine Pausa (solo se pausa iniziata e non finita) */}
                {myToday?.break_start && !myToday?.break_end && !myToday?.clock_out && (
                  <Button onClick={handleBreakEnd} className="bg-amber-600 hover:bg-amber-700" data-testid="break-end-btn">Fine Pausa</Button>
                )}
                {/* Step 4: Timbra Uscita (pausa completata o saltata, no uscita) */}
                {myToday?.clock_in && !myToday?.clock_out && (myToday?.break_end || (!myToday?.break_start)) && (
                  <Button onClick={handleClockOut} variant="destructive" data-testid="clock-out-btn">Timbra Uscita</Button>
                )}
                {myToday?.clock_out && (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 px-4 py-2"><CheckCircle size={14} className="mr-1" />Completata</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary for managers */}
      {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && todayData && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-slate-200 shadow-sm"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-slate-900">{todayData.total}</p><p className="text-xs text-slate-500">Totale</p></CardContent></Card>
          <Card className="border-slate-200 shadow-sm"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-green-600">{todayData.present}</p><p className="text-xs text-slate-500">Presenti</p></CardContent></Card>
          <Card className="border-slate-200 shadow-sm"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-red-600">{todayData.absent}</p><p className="text-xs text-slate-500">Assenti</p></CardContent></Card>
          <Card className="border-slate-200 shadow-sm"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-amber-600">{todayData.late}</p><p className="text-xs text-slate-500">Ritardi</p></CardContent></Card>
        </div>
      )}

      {/* Date selector + Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 mb-4">
            <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-48" data-testid="attendance-date" />
            <span className="text-sm text-slate-500">{records.length} registrazioni</span>
            {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && selectedIds.length > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <Badge variant="secondary">{selectedIds.length} selezionati</Badge>
                <Button size="sm" variant="outline" onClick={() => { setBulkForm({ clock_in: '', break_start: '', break_end: '', clock_out: '', site_id: '' }); setShowBulkEdit(true); }}><Edit size={14} className="mr-1" />Modifica</Button>
                <Button size="sm" variant="destructive" onClick={handleBulkDelete}><Trash2 size={14} className="mr-1" />Elimina</Button>
              </div>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && <TableHead className="w-10"><Checkbox checked={records.length > 0 && selectedIds.length === records.length} onCheckedChange={toggleAll} /></TableHead>}
                <TableHead>Dipendente</TableHead>
                <TableHead>Entrata</TableHead>
                <TableHead>Pausa</TableHead>
                <TableHead>Uscita</TableHead>
                <TableHead className="text-right">Ore Nette</TableHead>
                <TableHead>Stato</TableHead>
                {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && <TableHead className="w-24">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Nessuna registrazione per questa data</TableCell></TableRow>
              ) : records.map(r => (
                <TableRow key={r.id} className={`hover:bg-slate-50 ${selectedIds.includes(r.id) ? 'bg-blue-50' : ''}`} data-testid={`att-row-${r.id}`}>
                  {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && <TableCell><Checkbox checked={selectedIds.includes(r.id)} onCheckedChange={() => toggleSelect(r.id)} /></TableCell>}
                  <TableCell className="text-sm font-medium">{r.employee_name}
                    {r.created_by_name && r.created_by_name !== r.employee_name && <div className="text-[11px] font-normal text-slate-400" data-testid={`inserito-da-${r.id}`}>inserita da {r.created_by_name}</div>}
                    {r.updated_by_name && r.updated_by_name !== r.created_by_name && <div className="text-[11px] font-normal text-slate-400">modificata da {r.updated_by_name}</div>}
                  </TableCell>
                  <TableCell className="text-sm">{formatTime(r.clock_in)}</TableCell>
                  <TableCell className="text-sm">
                    {r.break_start ? (
                      <span>
                        {formatTime(r.break_start)}
                        {r.break_end ? ` - ${formatTime(r.break_end)}` : ' (in corso)'}
                        {r.break_minutes != null && <span className="text-xs text-slate-400 ml-1">({r.break_minutes}min)</span>}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-sm">{formatTime(r.clock_out)}</TableCell>
                  <TableCell className="text-sm text-right font-medium">{r.hours_worked ? `${r.hours_worked}h` : '-'}</TableCell>
                  <TableCell>
                    {r.clock_in && r.clock_out ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle size={12} className="mr-1" />Completa</Badge>
                    ) : r.clock_in ? (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">In corso</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200"><XCircle size={12} className="mr-1" />Assente</Badge>
                    )}
                  </TableCell>
                  {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)} className="p-1 h-auto text-blue-600 hover:text-blue-800" data-testid={`edit-att-${r.id}`}>
                          <Edit size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(r)} className="p-1 h-auto text-red-600 hover:text-red-800" data-testid={`del-att-${r.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Conferma Ore mensile - per preparare le buste paga senza chiamare uno per uno */}
      {isRole('admin', 'qhse', 'capo_cantiere') && (
        <Card className="border-slate-200 shadow-sm" data-testid="hours-confirmation-section">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-2">
              <MessageCircle size={18} className="text-[#1a4b8c]" />
              <h2 className="text-base font-semibold text-slate-800">Conferma Ore (per le buste paga)</h2>
            </div>
            <p className="text-xs text-slate-500">Chiedi ai dipendenti di verificare le ore di un mese prima di elaborare le buste paga. Ricevono una notifica in-app; nessun messaggio WhatsApp automatico &mdash; ti preparo un unico testo da incollare tu stesso nel gruppo.</p>

            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Mese</Label>
                <Input type="month" value={hcPeriod} onChange={(e) => setHcPeriod(e.target.value)} className="w-40" data-testid="hc-period" />
              </div>
              <div className="flex-1 min-w-[240px] space-y-1.5">
                <Label className="text-xs">Dipendenti (vuoto = tutti gli attivi con account)</Label>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto border border-slate-200 rounded-md p-2">
                  {employees.map(e => (
                    <button key={e.id} type="button" onClick={() => toggleHcEmployee(e.id)}
                      className={`text-xs px-2 py-1 rounded-full border ${hcSelectedEmployees.includes(e.id) ? 'bg-[#1a4b8c] text-white border-[#1a4b8c]' : 'bg-white text-slate-600 border-slate-200'}`}
                      data-testid={`hc-emp-${e.id}`}>
                      {e.first_name} {e.last_name}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={requestHoursConfirmation} disabled={hcRequesting} data-testid="hc-request-btn">
                <Send size={14} className="mr-1" /> {hcRequesting ? 'Invio...' : 'Richiedi conferma'}
              </Button>
            </div>

            {hcResult && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-2" data-testid="hc-result">
                <p className="text-sm text-blue-800">Notifica inviata a <b>{hcResult.created}</b> dipendenti.</p>
                {hcResult.skipped_senza_account?.length > 0 && (
                  <p className="text-xs text-amber-700">Senza account (non raggiunti): {hcResult.skipped_senza_account.join(', ')}</p>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={copyWhatsappMessage} data-testid="hc-copy-whatsapp">
                    <Copy size={13} className="mr-1" /> Copia messaggio per WhatsApp
                  </Button>
                  {hcResult.direct_link && (
                    <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(hcResult.direct_link).then(() => alert('Link copiato!'))} data-testid="hc-copy-link">
                      <Copy size={13} className="mr-1" /> Copia solo il link
                    </Button>
                  )}
                  <span className="text-xs text-slate-400">Incollalo tu stesso, una volta sola, nel gruppo</span>
                </div>
                {hcResult.direct_link && <p className="text-xs text-slate-500 font-mono break-all">{hcResult.direct_link}</p>}
              </div>
            )}

            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Dipendente</TableHead><TableHead className="text-right">Ore</TableHead><TableHead>Stato</TableHead><TableHead>Correzioni</TableHead><TableHead className="w-28"></TableHead></TableRow></TableHeader>
                <TableBody>
                  {hcLoadingList ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-4 text-slate-400 text-sm">Caricamento...</TableCell></TableRow>
                  ) : hcList.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-4 text-slate-400 text-sm">Nessuna richiesta per {hcPeriod}</TableCell></TableRow>
                  ) : hcList.map(h => (
                    <TableRow key={h.id} data-testid={`hc-row-${h.id}`}>
                      <TableCell className="text-sm font-medium">{h.employee_name}</TableCell>
                      <TableCell className="text-sm text-right">{h.total_hours}h</TableCell>
                      <TableCell><Badge variant="outline" className={HC_STATUS_MAP[h.status]?.cls}>{HC_STATUS_MAP[h.status]?.label || h.status}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {h.status === 'disputed' && h.corrections?.length > 0
                          ? h.corrections.map((c, i) => (
                              <div key={i}>
                                {c.date}: {c.hours_ordinarie || 0}h ord.
                                {c.hours_straordinarie ? ` + ${c.hours_straordinarie}h straord.` : ''}
                                {c.hours_notturne ? ` + ${c.hours_notturne}h nott.` : ''}
                                {c.note ? ` (${c.note})` : ''}
                              </div>
                            ))
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {h.status !== 'pending' && !h.admin_reviewed && (
                          <Button size="sm" variant="outline" onClick={() => markHcReviewed(h.id)} data-testid={`hc-review-${h.id}`}>Verificato</Button>
                        )}
                        {h.admin_reviewed && <Badge variant="outline" className="bg-slate-50 text-slate-400 text-[10px]">OK ufficio</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Riepilogo Presenze - vista mensile con filtri cantiere/dipendente */}
      {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && (
        <Card className="border-slate-200 shadow-sm" data-testid="attendance-summary-section">
          <CardContent className="p-4 space-y-4">
            <h2 className="text-base font-semibold text-slate-800">Riepilogo Presenze</h2>
            <p className="text-xs text-slate-500">Tutte le timbrature del mese in un colpo d'occhio, comprese le correzioni inviate dai dipendenti. Filtra per cantiere o dipendente per controllare solo quello che ti interessa.</p>

            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Mese</Label>
                <Input type="month" value={sumPeriod} onChange={(e) => setSumPeriod(e.target.value)} className="w-40" data-testid="sum-period" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Cantiere</Label>
                <Select value={sumSiteId} onValueChange={setSumSiteId}>
                  <SelectTrigger className="w-48" data-testid="sum-site"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Tutti i cantieri</SelectItem>
                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Dipendente</Label>
                <Select value={sumEmployeeId} onValueChange={setSumEmployeeId}>
                  <SelectTrigger className="w-48" data-testid="sum-employee"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Tutti i dipendenti</SelectItem>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <span className="text-xs text-slate-400">{sumRows.length} righe</span>
            </div>

            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Dipendente</TableHead><TableHead>Cantiere</TableHead><TableHead className="text-right">Ore registrate</TableHead><TableHead>Verifica</TableHead><TableHead>Straord.</TableHead></TableRow></TableHeader>
                <TableBody>
                  {sumLoading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-4 text-slate-400 text-sm">Caricamento...</TableCell></TableRow>
                  ) : sumRows.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-4 text-slate-400 text-sm">Nessuna timbratura per questi filtri</TableCell></TableRow>
                  ) : sumRows.map((r, i) => (
                    <TableRow key={`${r.employee_id}-${r.date}-${i}`} data-testid={`sum-row-${r.employee_id}-${r.date}`}>
                      <TableCell className="text-sm">{fmtDate(r.date)}</TableCell>
                      <TableCell className="text-sm font-medium">{r.employee_name}
                        {r.created_by_name && r.created_by_name !== r.employee_name && <div className="text-[11px] font-normal text-slate-400" data-testid={`sum-inserito-da-${r.employee_id}-${r.date}`}>inserita da {r.created_by_name}</div>}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{r.site_name || '-'}</TableCell>
                      <TableCell className="text-sm text-right">{r.hours_worked != null ? `${r.hours_worked}h` : '-'}</TableCell>
                      <TableCell className="text-xs">
                        {r.turnstile ? (
                          <div>
                            <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]" data-testid="sum-badge-tornello">Tornello</Badge>
                            <p className="text-slate-500">{r.turnstile.in_at.slice(11, 16)} - {r.turnstile.out_at.slice(11, 16)}</p>
                          </div>
                        ) : r.correction ? (
                          <div>
                            <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[10px] mb-0.5">Corretto dal dipendente</Badge>
                            <p className="text-slate-500">
                              {r.correction.hours_ordinarie || 0}h ord.
                              {r.correction.hours_straordinarie ? ` + ${r.correction.hours_straordinarie}h straord.` : ''}
                              {r.correction.hours_notturne ? ` + ${r.correction.hours_notturne}h nott.` : ''}
                              {r.correction.note ? ` — ${r.correction.note}` : ''}
                            </p>
                          </div>
                        ) : r.confirmation_status === 'confirmed' ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px]">Confermato</Badge>
                        ) : r.confirmation_status === 'disputed' ? (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">In revisione</Badge>
                        ) : r.confirmation_status === 'pending' ? (
                          <Badge variant="outline" className="bg-slate-100 text-slate-400 border-slate-200 text-[10px]">In attesa</Badge>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {(() => {
                          const cur = otMap[`${r.employee_id}|${r.date}`];
                          const realId = r.employee_id && !String(r.employee_id).startsWith('ts-');
                          const excess = r.hours_worked > 8 ? Math.floor((Math.round((r.hours_worked - 8) * 60) + 30) / 60) : 0;
                          if (!realId || (excess < 1 && !cur)) return <span className="text-slate-300">-</span>;
                          const counted = cur ? Math.min(Math.floor(cur.hours + 0.5), excess) : 0;
                          const pill = (on) => `px-2 py-0.5 rounded border text-[11px] ${on ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'}`;
                          return (
                            <div className="space-y-1" data-testid={`ot-cell-${r.employee_id}-${r.date}`}>
                              <div className="flex items-center gap-1">
                                <span className="text-slate-500">Straordinario autorizzato:</span>
                                <button type="button" className={pill(!cur)} onClick={() => cur && setOvertime(r, 0)} data-testid={`ot-no-${r.employee_id}-${r.date}`}>No</button>
                                <button type="button" className={pill(!!cur)} onClick={() => !cur && setOvertime(r, Math.max(1, excess))} data-testid={`ot-yes-${r.employee_id}-${r.date}`}>Si</button>
                                {cur && (
                                  <>
                                    <input
                                      key={`${r.employee_id}-${r.date}-${cur.hours}`} type="number" min="1" max="8" step="1" defaultValue={cur.hours}
                                      onBlur={(e) => Number(e.target.value) !== cur.hours && setOvertime(r, Number(e.target.value))}
                                      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                                      className="w-12 h-6 rounded border border-slate-300 px-1 text-center" data-testid={`ot-hours-${r.employee_id}-${r.date}`} />
                                    <span className="text-slate-500">ore</span>
                                  </>
                                )}
                              </div>
                              <p className="text-[10px] text-slate-500">
                                {cur ? `Conteggio: 8 ordinarie + ${counted} straord.` : 'Conteggio: 8 ordinarie, le ore oltre non valgono'}
                              </p>
                            </div>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Foglio presenze mensile */}
      <Dialog open={showSheet} onOpenChange={setShowSheet}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Foglio presenze mensile</DialogTitle>
            <DialogDescription>Compila il foglio Word con le ore di ogni dipendente (ore intere, massimo 8 al giorno).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="space-y-1.5">
              <Label>Mese</Label>
              <Select value={sheetMonth} onValueChange={setSheetMonth}>
                <SelectTrigger data-testid="sheet-month"><SelectValue /></SelectTrigger>
                <SelectContent>{monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4">
              <li>Ore del giorno: timbratura manuale di un responsabile, altrimenti PDF dei tornelli Sarpom, altrimenti timbratura del dipendente.</li>
              <li>Oltre le 8 ore contano 8, salvo straordinario richiesto o approvato da noi (colonna <b>Straord.</b> nel riepilogo). Sotto le 8 ore si arrotonda all'ora: da 30 minuti si sale.</li>
              <li><b>F</b> ferie, <b>P</b> permesso, <b>FR</b> ferie richieste. <b>V</b> = timbratura da verificare (incompleta o a zero ore): inserisci una presenza manuale per correggerla.</li>
            </ul>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
              <p className="font-medium">Tornelli Sarpom</p>
              <p className="text-xs text-slate-500" data-testid="ts-status">
                {tsStatus?.last_record_date
                  ? `Ultimo giorno importato: ${fmtDate(tsStatus.last_record_date)} (${tsStatus.total_records} timbrature in archivio)`
                  : 'Nessun report importato.'}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => refreshTurnstile(false)} disabled={sheetBusy} data-testid="ts-refresh-btn">
                  <RefreshCw size={14} className="mr-1" /> Aggiorna da email
                </Button>
                <input ref={tsFileRef} type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={uploadTurnstile} data-testid="ts-file-input" />
                <Button size="sm" variant="outline" onClick={() => tsFileRef.current?.click()} disabled={sheetBusy} data-testid="ts-upload-btn">
                  <Upload size={14} className="mr-1" /> Carica PDF
                </Button>
              </div>
            </div>
            {sheetMsg && <p className={sheetMsg.ok ? 'text-green-700' : 'text-red-600'} data-testid="sheet-msg">{sheetMsg.text}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSheet(false)}>Chiudi</Button>
            <Button onClick={downloadSheet} disabled={sheetBusy} data-testid="sheet-download-btn">
              {sheetBusy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileDown size={14} className="mr-1" />} Scarica Word
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Attendance Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Presenza</DialogTitle>
            <DialogDescription>Inserisci una presenza per un dipendente (anche per date passate)</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Dipendente *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="att-employee-select"><SelectValue placeholder="Seleziona dipendente..." /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="att-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Cantiere</Label>
              <Select value={form.site_id || "__none__"} onValueChange={(v) => setForm({ ...form, site_id: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessuno</SelectItem>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Ora Entrata</Label>
              <Input type="time" value={form.clock_in} onChange={(e) => setForm({ ...form, clock_in: e.target.value })} data-testid="att-clock-in" />
            </div>
            <div className="space-y-1.5">
              <Label>Inizio Pausa</Label>
              <Input type="time" value={form.break_start} onChange={(e) => setForm({ ...form, break_start: e.target.value })} data-testid="att-break-start" />
            </div>
            <div className="space-y-1.5">
              <Label>Fine Pausa</Label>
              <Input type="time" value={form.break_end} onChange={(e) => setForm({ ...form, break_end: e.target.value })} data-testid="att-break-end" />
            </div>
            <div className="space-y-1.5">
              <Label>Ora Uscita</Label>
              <Input type="time" value={form.clock_out} onChange={(e) => setForm({ ...form, clock_out: e.target.value })} data-testid="att-clock-out" />
            </div>
            <div className="space-y-1.5">
              <Label>Ore Lavorate (opzionale)</Label>
              <Input type="number" step="0.5" value={form.hours_worked} onChange={(e) => setForm({ ...form, hours_worked: e.target.value })} placeholder="Auto-calcolato se vuoto" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note aggiuntive..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={!form.employee_id || !form.date} data-testid="att-save-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Attendance Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Presenza</DialogTitle>
            <DialogDescription>Modifica la presenza di {editRecord?.employee_name}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Data *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Cantiere</Label>
              <Select value={form.site_id || "__none__"} onValueChange={(v) => setForm({ ...form, site_id: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessuno</SelectItem>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Ora Entrata</Label>
              <Input type="time" value={form.clock_in} onChange={(e) => setForm({ ...form, clock_in: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Inizio Pausa</Label>
              <Input type="time" value={form.break_start} onChange={(e) => setForm({ ...form, break_start: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Fine Pausa</Label>
              <Input type="time" value={form.break_end} onChange={(e) => setForm({ ...form, break_end: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Ora Uscita</Label>
              <Input type="time" value={form.clock_out} onChange={(e) => setForm({ ...form, clock_out: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Ore Lavorate (opzionale)</Label>
              <Input type="number" step="0.5" value={form.hours_worked} onChange={(e) => setForm({ ...form, hours_worked: e.target.value })} placeholder="Auto-calcolato se vuoto" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Assegnazione Ore a Progetti</Label>
              <div className="space-y-2">
                {(form.project_hours || []).map((ph, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Select value={ph.project_id || '__none__'} onValueChange={(v) => {
                      const updated = [...(form.project_hours || [])];
                      updated[idx] = { ...updated[idx], project_id: v === '__none__' ? '' : v, project_name: projects.find(p => p.id === v)?.name || '' };
                      setForm({ ...form, project_hours: updated });
                    }}>
                      <SelectTrigger className="flex-1 h-8 text-xs"><SelectValue placeholder="Progetto..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">--</SelectItem>
                        {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input type="number" step="0.5" className="w-20 h-8 text-xs" placeholder="Ore" value={ph.hours || ''} onChange={(e) => {
                      const updated = [...(form.project_hours || [])];
                      updated[idx] = { ...updated[idx], hours: e.target.value };
                      setForm({ ...form, project_hours: updated });
                    }} />
                    <Button variant="ghost" size="sm" className="p-1 h-6 text-red-500" onClick={() => {
                      setForm({ ...form, project_hours: (form.project_hours || []).filter((_, i) => i !== idx) });
                    }}><Trash2 size={12} /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="text-xs" onClick={() => setForm({ ...form, project_hours: [...(form.project_hours || []), { project_id: '', hours: '' }] })}>
                  <Plus size={12} className="mr-1" /> Aggiungi Progetto
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Annulla</Button>
            <Button onClick={handleEdit} disabled={!form.date} data-testid="att-edit-save-btn">Salva Modifiche</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={showBulkEdit} onOpenChange={setShowBulkEdit}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica {selectedIds.length} Presenze</DialogTitle>
            <DialogDescription>Compila solo i campi da modificare. I campi vuoti non verranno aggiornati.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-1.5"><Label>Entrata</Label><Input type="time" value={bulkForm.clock_in} onChange={(e) => setBulkForm({ ...bulkForm, clock_in: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Uscita</Label><Input type="time" value={bulkForm.clock_out} onChange={(e) => setBulkForm({ ...bulkForm, clock_out: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Inizio Pausa</Label><Input type="time" value={bulkForm.break_start} onChange={(e) => setBulkForm({ ...bulkForm, break_start: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Fine Pausa</Label><Input type="time" value={bulkForm.break_end} onChange={(e) => setBulkForm({ ...bulkForm, break_end: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5">
              <Label>Cantiere</Label>
              <Select value={bulkForm.site_id} onValueChange={(v) => setBulkForm({ ...bulkForm, site_id: v })}>
                <SelectTrigger><SelectValue placeholder="-- Non modificare --" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessuno</SelectItem>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkEdit(false)}>Annulla</Button>
            <Button onClick={handleBulkEdit}>Applica a {selectedIds.length}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
